import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import MultiStepForm from './components/MultiStepForm';
import { testimonials } from '../../mocks/testimonials';

const SERVICES = [
  { icon: 'ri-home-3-line', title: 'Residential Demolition', desc: 'Full or partial house demolition handled safely and efficiently for homeowners and builders across Melbourne.' },
  { icon: 'ri-building-4-line', title: 'Commercial Demolition', desc: 'Large-scale commercial and industrial site clearance with full compliance and project management.' },
  { icon: 'ri-plant-line', title: 'Arborist Report', desc: 'Expert consultation and reporting on trees affected by demolition or development works.' },
  { icon: 'ri-file-chart-line', title: 'Impact Assessment', desc: 'Comprehensive environmental and structural impact assessments for council submissions.' },
  { icon: 'ri-map-2-line', title: 'Land Assessment', desc: 'Detailed land condition and contamination assessments prior to development or demolition.' },
  { icon: 'ri-test-tube-line', title: 'Soil / Geotech Report', desc: 'Soil testing and geotechnical reporting to support safe and compliant construction projects.' },
];

const WHY_US = [
  { icon: 'ri-shield-check-line', title: 'Fully Licensed & Insured', desc: 'Every contractor holds current licences and full public liability insurance.' },
  { icon: 'ri-timer-flash-line', title: 'Fast Turnaround', desc: 'Receive your detailed quote within hours, not days. We respect your timeline.' },
  { icon: 'ri-price-tag-3-line', title: 'Transparent Pricing', desc: 'No hidden fees. Clear, itemised quotes so you know exactly what you\'re paying for.' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', desc: 'A project coordinator guides you from first enquiry through to site completion.' },
];

const PROCESS = [
  { num: '01', title: 'Submit Your Request', desc: 'Fill in the quick form with your service type, property address and contact details.' },
  { num: '02', title: 'We Review & Match', desc: 'Our team reviews your request and matches it to the best licensed contractor in your area.' },
  { num: '03', title: 'Receive Your Quote', desc: 'Get a detailed, transparent quote delivered to your inbox within hours.' },
  { num: '04', title: 'Project Begins', desc: 'Approve the quote and your project is scheduled. We manage the process end to end.' },
];

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function HomePage() {
  const [navScrolled, setNavScrolled] = useState(false);
  const servicesSec = useInView();
  const whySec = useInView();
  const processSec = useInView();
  const statsSec = useInView();
  const testimonialsSec = useInView();

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <main className="font-sans bg-white">

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_77ab5d59a81882e118d5873cfa699acb_bf1b6cbe.jpg"
            alt="Residential demolition site"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/92 via-navy/80 to-navy/60"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/60 via-transparent to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left: Hero copy */}
            <div className="flex flex-col justify-center">
              <div className="inline-flex items-center space-x-2 bg-orange-brand/20 border border-orange-brand/40 rounded-full px-4 py-1.5 mb-6 w-fit">
                <div className="w-2 h-2 rounded-full bg-orange-brand animate-pulse flex-shrink-0"></div>
                <span className="text-xs font-sans font-semibold text-orange-brand tracking-wide uppercase">
                  Melbourne's Trusted Demolition Platform
                </span>
              </div>

              <h1 className="font-heading font-extrabold text-white text-4xl xl:text-5xl leading-tight mb-5">
                Low-Cost, Fast &amp; Reliable<br />
                <span className="text-orange-brand">Demolition Services</span><br />
                Across Melbourne
              </h1>

              <p className="font-sans text-white/70 text-lg leading-relaxed mb-8">
                Residential &bull; Commercial &bull; Site Assessments
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                {['Licensed Contractors', 'No Obligation Quotes', 'Fast Response', 'Transparent Pricing'].map((tag) => (
                  <div key={tag} className="flex items-center space-x-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-orange-brand text-sm"></i>
                    </div>
                    <span className="text-sm font-sans text-white/85">{tag}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#quote-form"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-orange-brand text-white font-heading font-bold text-base rounded-xl hover:bg-orange-dark transition-all shadow-lg hover:shadow-orange-brand/30 whitespace-nowrap cursor-pointer"
                >
                  <span>Get a Quote</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 px-8 py-4 border-2 border-white/30 text-white font-heading font-semibold text-base rounded-xl hover:border-white/60 hover:bg-white/10 transition-all whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  <span>Call Us</span>
                </Link>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 mt-12 pt-8 border-t border-white/15">
                {[
                  { value: '1,200+', label: 'Projects Done' },
                  { value: '98%', label: 'Satisfaction Rate' },
                  { value: '<48hrs', label: 'Quote Delivery' },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-heading font-extrabold text-white text-2xl">{s.value}</p>
                    <p className="font-sans text-white/50 text-xs mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Multi-step form */}
            <div id="quote-form" className="w-full">
              <MultiStepForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section ref={servicesSec.ref} className="py-20 bg-grey-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${servicesSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-sans font-bold text-orange-brand uppercase tracking-widest mb-3">What We Offer</span>
            <h2 className="font-heading font-extrabold text-navy text-3xl xl:text-4xl mb-4">Our Services</h2>
            <p className="font-sans text-gray-500 max-w-xl mx-auto text-base">
              From full-scale demolition to specialist site reports — we cover every stage of your Melbourne project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((svc, i) => (
              <div
                key={svc.title}
                className={`bg-white rounded-2xl p-6 border border-grey-border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group ${
                  servicesSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-navy-50 rounded-xl mb-4 group-hover:bg-orange-light transition-colors">
                  <i className={`${svc.icon} text-navy text-2xl group-hover:text-orange-brand transition-colors`}></i>
                </div>
                <h3 className="font-heading font-bold text-navy text-base mb-2">{svc.title}</h3>
                <p className="font-sans text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                <Link
                  to="/services"
                  className="inline-flex items-center space-x-1 mt-4 text-orange-brand text-sm font-sans font-semibold hover:underline"
                >
                  <span>Learn more</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY DEMOHUB ── */}
      <section ref={whySec.ref} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 ${whySec.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="w-full h-[420px]">
                  <img
                    src="https://public.readdy.ai/ai/img_res/9c18d32259493c64004654be6ab6ae33.jpg"
                    alt="DemoHub professional team on site"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy-dark/90 to-transparent p-6">
                  <p className="font-heading font-bold text-white text-lg">Trusted by 1,200+ clients across Melbourne</p>
                  <p className="font-sans text-white/60 text-sm mt-1">Builders · Developers · Property Owners</p>
                </div>
                {/* Orange accent badge */}
                <div className="absolute top-5 left-5 bg-orange-brand text-white rounded-xl px-4 py-2 shadow-lg">
                  <p className="font-heading font-bold text-sm">98% Satisfaction</p>
                  <p className="font-sans text-white/80 text-xs">Verified client reviews</p>
                </div>
              </div>
            </div>

            <div className={`transition-all duration-700 delay-200 ${whySec.inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <span className="inline-block text-xs font-sans font-bold text-orange-brand uppercase tracking-widest mb-3">Why Choose Us</span>
              <h2 className="font-heading font-extrabold text-navy text-3xl xl:text-4xl mb-4 leading-tight">
                The Professional Standard<br />in Melbourne Demolition
              </h2>
              <p className="font-sans text-gray-500 text-base leading-relaxed mb-8">
                DemoHub connects you with Melbourne's most trusted demolition professionals. We handle the complexity so you can focus on your project.
              </p>

              <div className="space-y-5">
                {WHY_US.map((item) => (
                  <div key={item.title} className="flex items-start space-x-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-orange-light rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-orange-brand text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-navy text-sm mb-1">{item.title}</h4>
                      <p className="font-sans text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center space-x-2 mt-8 px-7 py-3.5 bg-navy text-white font-heading font-bold text-sm rounded-xl hover:bg-navy-light transition-all shadow-md whitespace-nowrap"
              >
                <span>About DemoHub</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BANNER ── */}
      <section ref={statsSec.ref} className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-building-line', value: '1,200+', label: 'Projects Completed' },
              { icon: 'ri-star-fill', value: '98%', label: 'Client Satisfaction' },
              { icon: 'ri-time-line', value: '<48hrs', label: 'Average Quote Time' },
              { icon: 'ri-group-line', value: '150+', label: 'Licensed Partners' },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className={`text-center transition-all duration-500 ${statsSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-orange-brand/20 border border-orange-brand/30 rounded-xl mx-auto mb-3">
                  <i className={`${stat.icon} text-orange-brand text-2xl`}></i>
                </div>
                <p className="font-heading font-extrabold text-white text-3xl mb-1">{stat.value}</p>
                <p className="font-sans text-white/50 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section ref={processSec.ref} className="py-20 bg-grey-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-14 transition-all duration-700 ${processSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-sans font-bold text-orange-brand uppercase tracking-widest mb-3">Simple Process</span>
            <h2 className="font-heading font-extrabold text-navy text-3xl xl:text-4xl mb-4">How It Works</h2>
            <p className="font-sans text-gray-500 max-w-xl mx-auto text-base">
              Four simple steps from request to project start. No hassle, no guesswork.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((step, i) => (
              <div
                key={step.num}
                className={`relative bg-white rounded-2xl p-6 border border-grey-border shadow-sm transition-all duration-500 ${
                  processSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {i < PROCESS.length - 1 && (
                  <div className="hidden lg:flex absolute top-10 -right-3 z-10 w-6 h-6 items-center justify-center">
                    <i className="ri-arrow-right-line text-orange-brand text-xl"></i>
                  </div>
                )}
                <div className="w-12 h-12 flex items-center justify-center bg-navy rounded-xl mb-4">
                  <span className="font-heading font-extrabold text-orange-brand text-sm">{step.num}</span>
                </div>
                <h3 className="font-heading font-bold text-navy text-base mb-2">{step.title}</h3>
                <p className="font-sans text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className={`text-center mt-10 transition-all duration-700 delay-500 ${processSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <a
              href="#quote-form"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-orange-brand text-white font-heading font-bold text-sm rounded-xl hover:bg-orange-dark transition-all shadow-md whitespace-nowrap cursor-pointer"
            >
              <span>Start Your Request Now</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section ref={testimonialsSec.ref} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${testimonialsSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-sans font-bold text-orange-brand uppercase tracking-widest mb-3">Client Reviews</span>
            <h2 className="font-heading font-extrabold text-navy text-3xl xl:text-4xl mb-4">What Our Customers Say</h2>
            <p className="font-sans text-gray-500 max-w-xl mx-auto text-base">
              Hear from builders, developers and property owners who've used DemoHub across Melbourne.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className={`bg-white border border-grey-border rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 ${
                  testimonialsSec.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, si) => (
                    <div key={si} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-orange-brand text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="font-sans text-gray-600 text-sm leading-relaxed mb-5 italic">"{t.review}"</p>
                <div className="flex items-center space-x-3 pt-4 border-t border-grey-border">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-navy-50">
                    <img src={t.avatar} alt={t.company} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-navy text-sm">{t.company}</p>
                    <p className="font-sans text-gray-400 text-xs">{t.identifier}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-20 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20construction%20site%20texture%20concrete%20rubble%20debris%20dark%20moody%20industrial%20background%20pattern&width=1440&height=400&seq=cta-bg-texture&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-orange-brand/20 border border-orange-brand/40 rounded-2xl mx-auto mb-6">
            <i className="ri-send-plane-2-line text-orange-brand text-3xl"></i>
          </div>
          <h2 className="font-heading font-extrabold text-white text-3xl xl:text-4xl mb-4">
            Ready to Get Started?
          </h2>
          <p className="font-sans text-white/60 text-base leading-relaxed mb-8 max-w-xl mx-auto">
            Submit your request today and receive a professional demolition quote within hours. No obligation, no hidden fees.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#quote-form"
              className="px-8 py-4 bg-orange-brand text-white font-heading font-bold text-sm rounded-xl hover:bg-orange-dark transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </a>
            <Link
              to="/contact"
              className="px-8 py-4 border-2 border-white/30 text-white font-heading font-semibold text-sm rounded-xl hover:border-white/60 hover:bg-white/10 transition-all whitespace-nowrap"
            >
              Talk to Our Team
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
